import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, FilePdf, PlayCircle } from "@phosphor-icons/react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { startCheckout } from "@/lib/checkout";
import { PACKAGES } from "@/lib/packages";

const TIER_LABELS = { core: "Core Curriculum" };

function VideoEmbed({ url, title }) {
  if (!url) {
    return (
      <div className="flex aspect-video items-center justify-center border border-[#2E2E2A] bg-[#1E1E1C] p-8 text-center text-sm text-[#A3A39C]" data-testid="module-video-placeholder">
        Lesson media is being uploaded — check back soon.
      </div>
    );
  }
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]+)/);
  if (yt) {
    return (
      <iframe
        src={`https://www.youtube.com/embed/${yt[1]}`}
        title={title}
        className="aspect-video w-full border border-[#2E2E2A]"
        allowFullScreen
        data-testid="module-video-embed"
      />
    );
  }
  return <video src={url} controls className="aspect-video w-full border border-[#2E2E2A] bg-black" data-testid="module-video-embed" />;
}

function HostedVideo({ moduleId, title }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.get(`/videos/sign/${moduleId}`)
      .then((r) => setUrl(`${BACKEND}${r.data.url}`))
      .catch(() => setFailed(true));
  }, [moduleId]);
  if (failed) {
    return <div className="flex aspect-video items-center justify-center border border-[#2E2E2A] bg-[#1E1E1C] p-8 text-sm text-[#A3A39C]">Video unavailable — please refresh or contact support.</div>;
  }
  if (!url) {
    return <div className="flex aspect-video items-center justify-center border border-[#2E2E2A] bg-[#1E1E1C] text-sm text-[#A3A39C]">Loading protected video...</div>;
  }
  return (
    <div>
      <video src={url} controls controlsList="nodownload" className="aspect-video w-full border border-[#2E2E2A] bg-black" data-testid="module-hosted-video" title={title} />
      <p className="mt-2 text-xs text-[#A3A39C]">Protected video — for security the playback link expires after 2 hours, but you can reload it anytime during your 30-day access.</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/modules").then((r) => setData(r.data)).catch(() => setData({ package: null, modules: [] }));
  }, []);

  const [communityLink, setCommunityLink] = useState("");
  useEffect(() => {
    if (user?.community_access) {
      api.get("/community/link").then((r) => setCommunityLink(r.data.link)).catch(() => {});
    }
  }, [user]);

  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    if (user && (user.packages?.length || user.package)) {
      api.get("/products/document-pack/member-files")
        .then((r) => setTemplates(r.data.files.map((f) => f.name)))
        .catch(() => {});
    }
  }, [user]);

  const openTemplate = async (name) => {
    try {
      const r = await api.get("/products/document-pack/member-files");
      const f = r.data.files.find((x) => x.name === name);
      if (f) window.open(`${BACKEND}${f.url}`, "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const owned = data?.packages || [];
  const canCoach = owned.some((id) => PACKAGES.find((p) => p.id === id)?.oneOnOne);
  const hasElite = owned.includes("inner_circle");
  const topPackage = PACKAGES.find((p) => p.id === data?.package);
  const grouped = [{ tier: "core", modules: data?.modules || [] }].filter((g) => g.modules.length > 0);

  return (
    <AppShell>
      <div data-testid="dashboard-page">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-[#2E2E2A] pb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#A3A39C]">Student Dashboard</p>
            <h1 data-testid="dashboard-welcome" className="mt-3 font-heading text-4xl font-light sm:text-5xl">
              Welcome back, {user?.name?.split(" ")[0]}.
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {data?.package ? (
              <span data-testid="current-package-badge" className="border border-[#C49A6C] px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#C49A6C]">
                {topPackage?.name}
              </span>
            ) : (
              <Link
                to="/#packages"
                data-testid="dashboard-enroll-cta"
                className="border border-[#C49A6C] bg-[#C49A6C] px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#141413] transition-colors duration-200 hover:bg-transparent hover:text-[#C49A6C]"
              >
                Enroll to unlock modules
              </Link>
            )}
          </div>
        </div>

        {data?.access_until && !data?.access_expired && (
          <p data-testid="access-until-note" className="mt-4 text-xs uppercase tracking-[0.15em] text-[#A3A39C]">
            Video access until {new Date(data.access_until).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </p>
        )}

        {data?.access_expired && (
          <div className="mt-8 border border-red-900/50 bg-[#1E1E1C] p-10" data-testid="access-expired-banner">
            <h2 className="font-heading text-2xl font-light">Your 30-day access has expired.</h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-[#A3A39C]">
              Video access locks 30 days after enrollment. Re-enroll in any package to unlock your modules again.
            </p>
            <a href="/#packages" data-testid="renew-access-button" className="mt-6 inline-block border border-[#C49A6C] bg-[#C49A6C] px-6 py-3 text-xs uppercase tracking-[0.15em] text-[#141413] transition-colors duration-200 hover:bg-transparent hover:text-[#C49A6C]">
              Renew Access
            </a>
          </div>
        )}

        {!data && <p className="py-16 text-[#A3A39C]" data-testid="dashboard-loading">Loading your curriculum...</p>}

        {data && data.modules.length === 0 && (
          <div className="mt-12 border border-[#2E2E2A] bg-[#1E1E1C] p-12" data-testid="dashboard-upsell">
            <h2 className="font-heading text-3xl font-light">Your modules are waiting.</h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#A3A39C]">
              Pick a package to unlock the recorded curriculum, slide decks, and coaching sessions.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-px border border-[#2E2E2A] bg-[#2E2E2A] sm:grid-cols-2 xl:grid-cols-3">
              {PACKAGES.map((pkg) => (
                <div key={pkg.id} className="bg-[#1E1E1C] p-6" data-testid={`upsell-${pkg.id}`}>
                  <p className="font-heading text-2xl">{pkg.name}</p>
                  <p className="mt-1 font-heading text-xl text-[#C49A6C]">${pkg.price.toLocaleString()}</p>
                  <button
                    data-testid={`upsell-${pkg.id}-button`}
                    onClick={() => startCheckout(pkg.id, user, navigate)}
                    className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#C49A6C] transition-colors duration-200 hover:text-[#F4F4F0]"
                  >
                    Enroll <ArrowRight size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {grouped.map((g) => (
          <section key={g.tier} className="mt-12" data-testid={`tier-section-${g.tier}`}>
            <h2 className="mb-6 text-xs uppercase tracking-[0.2em] text-[#A3A39C]">{TIER_LABELS[g.tier]}</h2>
            <div className="divide-y divide-[#2E2E2A] border border-[#2E2E2A]">
              {g.modules.map((m) => {
                const isOpen = expanded === m.id;
                return (
                  <div key={m.id} className="bg-[#1E1E1C]" data-testid={`module-${m.order}`}>
                    <button
                      data-testid={`module-${m.order}-toggle`}
                      onClick={() => setExpanded(isOpen ? null : m.id)}
                      className="flex w-full items-center gap-6 px-6 py-5 text-left transition-colors duration-200 hover:bg-[#252522]"
                    >
                      <span className="font-heading text-2xl text-[#C49A6C]">{String(m.order).padStart(2, "0")}</span>
                      <span className="flex-1">
                        <span className="block font-heading text-xl">{m.title}</span>
                        <span className="mt-1 block text-sm text-[#A3A39C]">{m.description}</span>
                      </span>
                      {m.duration && <span className="hidden text-xs text-[#A3A39C] sm:block">{m.duration}</span>}
                      <PlayCircle size={24} weight={isOpen ? "fill" : "duotone"} className="shrink-0 text-[#C49A6C]" />
                    </button>
                    {isOpen && (
                      <div className="grid grid-cols-1 gap-6 border-t border-[#2E2E2A] p-6 lg:grid-cols-3">
                        <div className="lg:col-span-2">
                          {m.video_file ? <HostedVideo moduleId={m.id} title={m.title} /> : <VideoEmbed url={m.video_url} title={m.title} />}
                        </div>
                        <div className="flex flex-col gap-4">
                          {m.workbook_file ? (
                            <button
                              data-testid={`module-${m.order}-workbook`}
                              onClick={async () => {
                                const win = window.open("", "_blank");
                                try {
                                  const r = await api.get(`/workbooks/sign/${m.id}`);
                                  if (win) win.location.href = `${BACKEND}${r.data.url}`;
                                } catch (e) {
                                  if (win) win.close();
                                  toast.error(formatApiError(e));
                                }
                              }}
                              className="inline-flex items-center gap-2 border border-[#C49A6C] px-5 py-3 text-xs uppercase tracking-[0.15em] text-[#C49A6C] transition-colors duration-200 hover:bg-[#C49A6C] hover:text-[#141413]"
                            >
                              <FilePdf size={16} /> Print Workbook (PDF)
                            </button>
                          ) : m.slides_url ? (
                            <a
                              href={m.slides_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`module-${m.order}-slides`}
                              className="inline-flex items-center gap-2 border border-[#C49A6C] px-5 py-3 text-xs uppercase tracking-[0.15em] text-[#C49A6C] transition-colors duration-200 hover:bg-[#C49A6C] hover:text-[#141413]"
                            >
                              <FilePdf size={16} /> Download Workbook
                            </a>
                          ) : (
                            <span className="inline-flex items-center gap-2 border border-[#2E2E2A] px-5 py-3 text-xs uppercase tracking-[0.15em] text-[#A3A39C]">
                              <FilePdf size={16} /> Workbook coming soon
                            </span>
                          )}
                          <p className="text-sm leading-relaxed text-[#A3A39C]">{m.description}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {owned.length > 0 && !hasElite && (
          <div className="mt-12 flex flex-wrap items-center justify-between gap-6 border border-[#C49A6C]/40 bg-[#1E1E1C] p-8" data-testid="upgrade-banner">
            <div>
              <h3 className="font-heading text-2xl font-light">Ready for the next level?</h3>
              <p className="mt-2 text-sm text-[#A3A39C]">
                Explore higher-touch packages — from group coaching to Inner Circle with in-the-field shadowing.
              </p>
            </div>
            <button
              data-testid="upgrade-button"
              onClick={() => { window.location.href = "/#packages"; }}
              className="inline-flex items-center gap-2 border border-[#C49A6C] bg-[#C49A6C] px-6 py-3 text-xs uppercase tracking-[0.15em] text-[#141413] transition-colors duration-200 hover:bg-transparent hover:text-[#C49A6C]"
            >
              Explore Packages <ArrowRight size={14} />
            </button>
          </div>
        )}

        {canCoach && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-6 border border-[#2E2E2A] bg-[#1E1E1C] p-8" data-testid="book-session-banner">
            <div>
              <h3 className="font-heading text-2xl font-light">Your package includes live training.</h3>
              <p className="mt-2 text-sm text-[#A3A39C]">Book a {hasElite ? "1-on-1 or in-the-field" : "1-on-1"} session with Ciara.</p>
            </div>
            <Link
              to="/bookings"
              data-testid="book-session-link"
              className="inline-flex items-center gap-2 border border-[#F4F4F0]/30 px-6 py-3 text-xs uppercase tracking-[0.15em] transition-colors duration-200 hover:border-[#C49A6C] hover:text-[#C49A6C]"
            >
              Book a Session <ArrowRight size={14} />
            </Link>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-between gap-6 border border-[#2E2E2A] bg-[#1E1E1C] p-8" data-testid="addons-banner">
          <div>
            <h3 className="font-heading text-2xl font-light">Add-on Services</h3>
            <p className="mt-2 max-w-lg text-sm leading-relaxed text-[#A3A39C]">
              Consultations, 1-on-1 coaching calls, the Document Pack, Done-for-You deal finding, and the investor community — purchase anytime without leaving your account.
            </p>
          </div>
          <Link
            to="/services"
            data-testid="dashboard-services-link"
            className="inline-flex items-center gap-2 border border-[#C49A6C] px-6 py-3 text-xs uppercase tracking-[0.15em] text-[#C49A6C] transition-colors duration-200 hover:bg-[#C49A6C] hover:text-[#141413]"
          >
            Browse Services <ArrowRight size={14} />
          </Link>
        </div>

        {templates.length > 0 && !data?.access_expired && (
          <div className="mt-8 border border-[#2E2E2A] bg-[#1E1E1C] p-8" data-testid="templates-section">
            <h3 className="font-heading text-2xl font-light">Templates &amp; Documents</h3>
            <p className="mt-2 text-sm text-[#A3A39C]">Included with your package — download anytime during your access.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              {templates.map((t, i) => (
                <button
                  key={i}
                  data-testid={`template-${i}`}
                  onClick={() => openTemplate(t)}
                  className="inline-flex items-center gap-2 border border-[#C49A6C] px-5 py-3 text-xs uppercase tracking-[0.15em] text-[#C49A6C] transition-colors duration-200 hover:bg-[#C49A6C] hover:text-[#141413]"
                >
                  <FilePdf size={16} /> {t}
                </button>
              ))}
            </div>
          </div>
        )}

        {user?.community_access && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-6 border border-[#2E2E2A] bg-[#1E1E1C] p-8" data-testid="community-banner">
            <div>
              <h3 className="font-heading text-2xl font-light">Community &amp; Networking</h3>
              <p className="mt-2 text-sm text-[#A3A39C]">
                {communityLink
                  ? "Your investor community is ready — jump in."
                  : "You're on the list — Ciara sends the community invite link after approval. You're also subscribed to the monthly newsletter."}
              </p>
            </div>
            {communityLink && (
              <a
                href={communityLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="community-link-button"
                className="inline-flex items-center gap-2 border border-[#C49A6C] px-6 py-3 text-xs uppercase tracking-[0.15em] text-[#C49A6C] transition-colors duration-200 hover:bg-[#C49A6C] hover:text-[#141413]"
              >
                Open Community <ArrowRight size={14} />
              </a>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
